import * as yup from "yup";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState, useEffect, useCallback } from "react";
import { IoSaveOutline } from "react-icons/io5";
import { FaCircleNotch } from "react-icons/fa";

import {
  IMAGE_MIME_TYPES,
  IMAGE_MIME_TYPES_PLACEHOLDER,
} from "../../../../../utils/constants";
import { getBase64 } from "../../../../../utils/stringUtils";

import Toaster from "../../../../../components/common/Toaster";
import ReactSelect from "../../../../../components/common/ReactSelect";
import Textbox from "../../../../../components/common/Textbox";
import FileUpload from "../../../../../components/common/FileUpload";

import accountService from "../../../../../services/accountService";

const IdentificationForm = ({ data, id, isLoading, setLoading, onSuccess }) => {
  const [types, setTypes] = useState([]);

  let schema = yup.object({
    number: yup.string().required("Please enter ID no."),
    front: yup.mixed().required("Please upload the front image of your ID."),
    back: yup.mixed().required("Please upload the back image of your ID."),
    identity_document_type_id: yup.object().shape({
      label: yup.string().required("Please select type."),
      value: yup.string().required("Please select type."),
    }),
  });

  if (data) {
    schema = yup.object({
      number: yup.string().required("Please enter ID no."),
      identity_document_type_id: yup.object().shape({
        label: yup.string().required("Please select type."),
        value: yup.string().required("Please select type."),
      }),
    });
  }

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const item = { ...info };

      item["account_id"] = id;
      item["identity_document_type_id"] = item.identity_document_type_id.value;

      if (data) item["id"] = data.id;

      if (!data) {
        const front_file = await getBase64(info.front[0]);
        const front_content = front_file.split(",")[1];

        const back_file = await getBase64(info.back[0]);
        const back_content = back_file.split(",")[1];

        const front = {
          file_name: item.front[0].name,
          content: front_content,
          type: 1,
        };

        const back = {
          file_name: item.back[0].name,
          content: back_content,
          type: 2,
        };

        item["images"] = [front, back];
      }

      await accountService.saveAccountIdentityDocumentAsync(item);
      Toaster.Success("ID added successfully");

      onSuccess();
      setLoading(false);
    } catch (ex) {
      Toaster.Error("Something went wrong.");
      setLoading(false);
    }
  };

  const fetchTypes = useCallback(async (mounted) => {
    try {
      const result = await accountService.getIdentityDocumentTypesAsync();

      if (mounted) {
        const items = result.map((item) => {
          const container = {};
          container["label"] = item.alternate_name;
          container["value"] = item.id;

          return container;
        });

        setTypes(items);
      }
    } catch (ex) {
      Toaster.Error("Something went wrong.");
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    fetchTypes(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchTypes]);

  useEffect(() => {
    const populateForm = () => {
      const selectedOption = {
        label: data.alternate_type_name,
        value: data.identity_document_type_id,
      };

      setValue("identity_document_type_id", selectedOption);
      setValue("number", data.number);
    };

    if (data) populateForm();
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <ReactSelect
            id="types-dropdown"
            name="identity_document_type_id"
            title="ID Type"
            options={types}
            control={control}
            register={register("identity_document_type_id")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <Textbox
            id="number-textbox"
            name="number"
            type="text"
            title="ID No."
            register={register("number")}
            errors={errors}
          />
        </div>
      </div>
      {!data && (
        <>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <FileUpload
                id="front-upload"
                name="front"
                title="Front ID"
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                errors={errors}
                accepts={IMAGE_MIME_TYPES}
                placeholder={IMAGE_MIME_TYPES_PLACEHOLDER}
                maxFiles={1}
                maxSize={5242880}
                multiple={false}
              />
            </div>
          </div>
          <div className="grid grid-cols-2">
            <div className="col-span-2">
              <FileUpload
                id="back-upload"
                name="back"
                title="Back ID"
                setValue={setValue}
                clearErrors={clearErrors}
                register={register}
                errors={errors}
                accepts={IMAGE_MIME_TYPES}
                placeholder={IMAGE_MIME_TYPES_PLACEHOLDER}
                maxFiles={1}
                maxSize={5242880}
                multiple={false}
              />
            </div>
          </div>
        </>
      )}
      <div className="grid grid-cols-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default IdentificationForm;
